import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Skeleton,
  Typography,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { ServicesStatusPayload, ServiceComponent } from './types';
import {
  EXPOSE_SERVICE_KEYS,
  formatSecondsAgo,
  statusToChipColor,
} from './helpers';
import { deriveServiceHealth } from '@/lib/serviceHealth';

type Props = {
  data: ServicesStatusPayload | undefined;
  isLoading: boolean;
  error: Error | null;
  lastFetchedAt: number | null;
};

function getExposeComponent(
  key: string,
  components: Record<string, ServiceComponent>,
  statusFetchOk: boolean
): ServiceComponent | null {
  if (components[key]) return components[key];
  if (key === 'orchestrator-api' && statusFetchOk) {
    return {
      status: 'ok',
      ok: true,
      latency_ms: 0,
      status_code: 200,
      observed_at: new Date().toISOString(),
      reason: 'inferred_from_status_endpoint',
    };
  }
  return null;
}

export default function StatusBar({ data, isLoading, error, lastFetchedAt }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const components = data?.components || {};
  const health = deriveServiceHealth(data);
  const summary = health.total > 0
    ? { total: health.total, ok: health.ok, degraded: health.degraded, down: health.down }
    : data?.summary;

  const rows = useMemo(
    () =>
      EXPOSE_SERVICE_KEYS.map((key) => ({
        key,
        component: getExposeComponent(key, components, Boolean(data)),
      })),
    [components, data]
  );

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width={120} height={32} />
        ))}
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Platform status unavailable: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Platform backbone</Typography>
        <Typography variant="caption" color="text.secondary">
          {summary ? (
            <>
              {summary.total} services · {summary.ok} ok · {summary.degraded} degraded · {summary.down} down
              {lastFetchedAt ? ` · last checked ${formatSecondsAgo(lastFetchedAt)}` : ''}
            </>
          ) : (
            '—'
          )}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {rows.map(({ key, component }) => {
          const status = component?.status || 'unknown';
          const isOpen = expanded === key;
          return (
            <Box key={key}>
              <Chip
                label={`${key}: ${status}`}
                color={statusToChipColor(status)}
                size="small"
                onClick={() => setExpanded(isOpen ? null : key)}
                deleteIcon={<ExpandMoreIcon />}
                onDelete={() => setExpanded(isOpen ? null : key)}
                variant={isOpen ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
              <Collapse in={isOpen && Boolean(component)}>
                {component && (
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                      fontSize: 12,
                      fontFamily: 'monospace',
                    }}
                  >
                    <div>latency_ms: {component.latency_ms}</div>
                    <div>status_code: {component.status_code}</div>
                    <div>observed_at: {component.observed_at}</div>
                    {component.reason && <div>reason: {component.reason}</div>}
                  </Box>
                )}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
